import React, { useEffect, useState, useCallback } from "react";
import { api, fmtNum, curSym, fmtDate, errMsg, fileUrl } from "@finance/lib/api";
import { toast } from "sonner";
import { PrintButton } from "@finance/components/PrintButton";
import { Button } from "@finance/components/ui/button";
import { Input } from "@finance/components/ui/input";
import { DateField } from "@finance/components/DateField";
import { Label } from "@finance/components/ui/label";
import { Textarea } from "@finance/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@finance/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@finance/components/ui/select";
import { DownloadSimple, Trash, Package, Truck, Receipt, FileText, Scan, CircleNotch, ArrowUUpLeft, ArrowUUpRight, PencilSimple } from "@phosphor-icons/react";
import { DocNumberField } from "@finance/components/DocNumberField";
import { CreditDebitNotes } from "@finance/components/CreditDebitNotes";
import { DocumentPreview } from "@finance/components/DocumentPreview";
import { ReverseActions } from "@finance/components/ReverseActions";
import { FxRateField } from "@finance/components/FxRateField";
import { EfdTimeField } from "@finance/components/EfdTimeField";
import { LocalCharges } from "@finance/components/LocalCharges";
import { useAuth } from "@finance/context/AuthContext";
import { FulfillmentKpis } from "@finance/components/FulfillmentKpis";

// Fulfillment panel: Goods Received (GRV → stock in), Delivery Notes (stock out),
// itemized Client Invoices, and itemized Supplier Invoice matching (GRNI → Payables).
export default function Fulfillment({ transaction, onChanged, onStats, onAdjustments }) {
  const tid = transaction?.id;
  const { user } = useAuth();
  const canAuthorizeFulfillment = !!(user?.is_owner || user?.role_code === "FM");
  const [resubmit, setResubmit] = useState(null);
  const [data, setData] = useState(null);
  const [mode, setMode] = useState(null); // 'grv' | 'dn' | 'invoice' | 'sup'
  const [signer, setSigner] = useState("");
  const [docDate, setDocDate] = useState("");
  const [notes, setNotes] = useState("");
  const [qty, setQty] = useState({});
  const [prices, setPrices] = useState({});
  const [banks, setBanks] = useState([]);
  const [bank, setBank] = useState("none");
  const [efdCode, setEfdCode] = useState("");
  const [efdTime, setEfdTime] = useState("");
  const [supInvNo, setSupInvNo] = useState("");
  const [docNo, setDocNo] = useState("");
  const [clientPo, setClientPo] = useState("");
  const [clientPos, setClientPos] = useState([]);
  const [creditDays, setCreditDays] = useState("");
  const [fxRate, setFxRate] = useState("");
  const [glDate, setGlDate] = useState("");
  const [whtApplicable, setWhtApplicable] = useState(false);
  const [whtRate, setWhtRate] = useState("5");
  const [whtMethod, setWhtMethod] = useState("net_down");
  const [ocrLoading, setOcrLoading] = useState(false);
  const [match, setMatch] = useState(null);
  const [payView, setPayView] = useState(null);
  const [retData, setRetData] = useState(null);
  const [retMode, setRetMode] = useState(null); // 'client' | 'supplier'
  const [retQty, setRetQty] = useState({});
  const [retMeta, setRetMeta] = useState({});
  const [editInv, setEditInv] = useState(null); // client invoice being edited (null = create)
  const [editSup, setEditSup] = useState(null); // supplier invoice DRAFT being edited (null = create)
  const [preview, setPreview] = useState(false);
  const [scopeCpo, setScopeCpo] = useState("");
  const [retScopeCpo, setRetScopeCpo] = useState("");

  const load = useCallback(() => {
    if (!tid) return;
    api.get(`/transactions/${tid}/fulfillment`).then((r) => { setData(r.data); onStats?.(r.data); }).catch(() => {});
    api.get(`/transactions/${tid}/supplier-match`).then((r) => setMatch(r.data)).catch(() => {});
    api.get(`/transactions/${tid}/returns-notes`).then((r) => { setRetData(r.data); onAdjustments?.(r.data); }).catch(() => {});
  }, [tid, onStats, onAdjustments]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/bank-accounts").then((r) => setBanks(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const cid = transaction?.client_id;
    api.get("/client-pos", { params: cid ? { client_id: cid } : {} }).then((r) => setClientPos(r.data || [])).catch(() => {});
  }, [transaction?.client_id]);

  const openDialog = (m) => {
    setEditInv(null); setEditSup(null); setScopeCpo("");
    setMode(m); setFxRate(""); setSigner(""); setNotes(""); setQty({}); setEfdCode(""); setEfdTime(""); setSupInvNo(""); setDocNo("");
    setWhtApplicable(false); setWhtRate("5"); setCreditDays(""); setGlDate("");
    setDocDate(new Date().toISOString().slice(0, 10));
    setBank(banks[0]?.id || "none"); setClientPo(transaction?.client_po_no || "");
    const p = {};
    (data?.line_items || []).forEach((l) => { p[l.line_index] = (m === "invoice" && l.client_po_price != null) ? l.client_po_price : l.unit_price; });
    setPrices(p);
    // Pre-select the most recent Client PO when this Somcore PO consolidates exactly two.
    const cpos = data?.client_pos || [];
    if ((m === "dn" || m === "invoice") && cpos.length === 2) { setScopeCpo(cpos[0].id); setClientPo(cpos[0].no || ""); }
  };

  const openEditInvoice = (inv) => {
    setEditInv(inv);
    setMode("invoice"); setSigner(""); setSupInvNo("");
    setNotes(inv.notes || "");
    setEfdCode(inv.lx_efd_qr_code || ""); setEfdTime(inv.lx_efd_time || "");
    setDocNo(inv.invoice_no || "");
    setDocDate((inv.date || "").slice(0, 10) || new Date().toISOString().slice(0, 10));
    setClientPo(inv.client_po_no || "");
    setCreditDays(inv.credit_days != null ? String(inv.credit_days) : "");
    setBank(inv.bank_account_id || "none");
    setWhtApplicable(!!inv.wht_applicable); setWhtRate(inv.wht_rate != null ? String(inv.wht_rate) : "5"); setWhtMethod(inv.wht_method || "net_down");
    const q = {}, p = {};
    (inv.items || []).forEach((it) => { q[it.line_index] = it.qty; p[it.line_index] = it.unit_price; });
    setQty(q); setPrices(p);
  };

  const openEditSupplier = (inv) => {
    setEditSup(inv); setEditInv(null);
    setMode("sup"); setSigner("");
    setNotes(inv.notes || "");
    setSupInvNo(inv.supplier_invoice_no || "");
    setEfdCode(inv.efd_qr_code || ""); setEfdTime(inv.efd_time || "");
    setDocNo(inv.invoice_no || "");
    setDocDate((inv.date || "").slice(0, 10) || new Date().toISOString().slice(0, 10));
    setCreditDays(inv.credit_days != null ? String(inv.credit_days) : "");
    setGlDate((inv.gl_date || "").slice(0, 10) || "");
    setWhtApplicable(!!inv.wht_applicable); setWhtRate(inv.wht_rate != null ? String(inv.wht_rate) : "5"); setWhtMethod(inv.wht_method || "net_down");
    const q = {}, p = {};
    (inv.items || []).forEach((it) => { q[it.line_index] = it.qty; p[it.line_index] = it.unit_price; });
    setQty(q); setPrices(p);
  };

  const saveSup = async (asDraft) => {
    try {
      const items = (data?.line_items || [])
        .filter((l) => Number(qty[l.line_index]) > 0)
        .map((l) => ({ line_index: l.line_index, qty: Number(qty[l.line_index]),
          description: l.description, sku: l.sku, item_id: l.item_id,
          unit_price: Number(prices[l.line_index]) || 0, tax_type: l.supplier_tax_type || l.tax_type }));
      if (items.length === 0) { toast.error("Enter a quantity for at least one line."); return false; }
      const payload = {
        currency: data?.currency || "TZS", vat_rate: data?.vat_rate ?? 18, date: docDate || null,
        wht_applicable: whtApplicable, wht_rate: Number(whtRate) || 0, wht_method: whtMethod,
        supplier_invoice_no: supInvNo || null, efd_qr_code: efdCode || null, efd_time: efdTime || null,
        credit_days: creditDays !== "" ? Number(creditDays) : null, gl_date: glDate || null,
        doc_no: docNo || null, notes, items, save_draft: asDraft,
      };
      let recon;
      if (editSup) {
        await api.put(`/supplier-invoices/${editSup.id}`, payload);
        if (asDraft) { toast.success("Draft saved"); }
        else { const r = await api.post(`/supplier-invoices/${editSup.id}/post`); recon = r.data?.cost_reconciliation; toast.success("Supplier invoice confirmed & posted to GL"); }
      } else {
        const r = await api.post(`/transactions/${tid}/supplier-invoice`, payload);
        recon = r.data?.cost_reconciliation;
        toast.success(asDraft ? "Supplier invoice draft saved" : "Supplier invoice matched & posted to GL");
      }
      if (recon?.reason === "period_closed") toast.warning(`Actual cost differs from the PO estimate, but ${recon.blocked_periods?.join(", ")} is locked/closed — AVCO was NOT rewritten. Post a prior-period adjustment instead.`);
      else if (recon?.applied) toast.success("Inventory AVCO & COGS reconciled to the actual supplier cost.");
      setMode(null); setEditSup(null); load(); onChanged?.();
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };

  const postDraft = async (id) => {
    if (!window.confirm("Confirm and post this supplier invoice to the GL? Once posted it becomes view-only.")) return;
    try {
      const r = await api.post(`/supplier-invoices/${id}/post`);
      toast.success("Supplier invoice posted to GL");
      const rec = r.data?.cost_reconciliation;
      if (rec?.reason === "period_closed") toast.warning(`Actual cost differs from the PO estimate, but ${rec.blocked_periods?.join(", ")} is locked/closed — AVCO was NOT rewritten.`);
      else if (rec?.applied) toast.success("Inventory AVCO & COGS reconciled to the actual supplier cost.");
      load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const saveClient = async (asDraft) => {
    try {
      const items = (data?.line_items || [])
        .filter((l) => Number(qty[l.line_index]) > 0)
        .map((l) => ({ line_index: l.line_index, qty: Number(qty[l.line_index]),
          description: l.description, sku: l.sku, item_id: l.item_id,
          unit_price: Number(prices[l.line_index]) || 0, tax_type: l.tax_type }));
      if (items.length === 0) { toast.error("Enter a quantity for at least one line."); return false; }
      if (!asDraft && (!efdCode || !efdTime)) { toast.error("Enter the EFD verification code and timestamp before confirming & posting to the GL."); return false; }
      if (!asDraft && creditDays === "") { toast.error("Credit days are required."); return false; }
      const payload = {
        currency: data?.client_currency || data?.currency || "TZS", vat_rate: (data?.vat_rate || 18), date: docDate || null, fx_rate: Number(fxRate) || null,
        wht_applicable: whtApplicable, wht_rate: Number(whtRate) || 0, wht_method: whtMethod,
        client_id: transaction?.client_id || null, client_po_no: clientPo || null,
        credit_days: creditDays !== "" ? Number(creditDays) : null,
        lx_efd_qr_code: efdCode || null, lx_efd_time: efdTime || null,
        bank_account_id: bank !== "none" ? bank : null, doc_no: docNo || null, notes, items, save_draft: asDraft,
      };
      if (editInv) {
        await api.put(`/client-invoices/${editInv.id}`, payload);
        if (asDraft) { toast.success("Draft saved"); }
        else { await api.post(`/client-invoices/${editInv.id}/post`, { fx_rate: Number(fxRate) || null, invoice_no: docNo || null }); toast.success("Client invoice confirmed & posted to GL"); }
      } else {
        await api.post(`/transactions/${tid}/client-invoice`, payload);
        toast.success(asDraft ? "Client invoice draft saved" : "Client invoice posted to GL");
      }
      setMode(null); setEditInv(null); load(); onChanged?.();
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };

  const setFxFor = async (inv) => {
    const rate = window.prompt(`Set FX rate for invoice ${inv.invoice_no} (TZS per 1 ${inv.currency}):`, inv.fx_rate || "");
    if (!rate) return;
    try {
      const p = (await api.post(`/client-invoices/${inv.id}/set-fx-rate`, { rate: Number(rate), preview: true })).data;
      if (!window.confirm(`New TZS total ≈ TZS ${fmtNum(p.new_total_tzs)}. Post a correcting journal (dated ${String(inv.date).slice(0, 10)}) with ${p.lines.length} line(s)?`)) return;
      await api.post(`/client-invoices/${inv.id}/set-fx-rate`, { rate: Number(rate) });
      toast.success("FX correction posted"); load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const postClientDraft = async (id) => {
    if (!window.confirm("Confirm and post this client invoice to the GL? Once posted it becomes view-only.")) return;
    const inv = (data?.client_invoices || []).find((x) => x.id === id) || {};
    let rate = inv.fx_rate;
    if ((inv.currency || "TZS") !== "TZS" && !(Number(rate) > 1)) {
      rate = window.prompt(`Enter the FX rate for this invoice (TZS per 1 ${inv.currency}) — mandatory:`);
      if (!rate) return;
    }
    try {
      await api.post(`/client-invoices/${id}/post`, { fx_rate: Number(rate) || null });
      toast.success("Client invoice posted to GL");
      load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remainKey = { grv: "receivable", dn: "deliverable", invoice: "invoiceable", sup: "supplier_invoiceable" }[mode];
  const usePrice = mode === "invoice" || mode === "sup";
  const isImport = !!(transaction?.is_import || transaction?.is_import_supplier || data?.is_import);
  const editQtyMap = {};
  (editInv?.items || []).forEach((it) => { editQtyMap[it.line_index] = (editQtyMap[it.line_index] || 0) + (it.qty || 0); });
  const lineRemaining = (l) => (editInv && mode === "invoice") ? Math.round((l.invoiceable + (editQtyMap[l.line_index] || 0)) * 10000) / 10000 : l[remainKey];

  // Distinct Client POs consolidated onto this Somcore PO (each line carries the CPO it was created under).
  const poOptions = React.useMemo(() => {
    if (data?.client_pos?.length) return data.client_pos;
    const m = new Map();
    (data?.line_items || []).forEach((l) => {
      if (l.client_po_id && !m.has(l.client_po_id)) {
        const po = clientPos.find((p) => p.id === l.client_po_id);
        m.set(l.client_po_id, { id: l.client_po_id, no: l.client_po_no || po?.client_po_no || "—", name: po?.client_name || "" });
      }
    });
    return [...m.values()];
  }, [data, clientPos]);
  const multiCpo = poOptions.length > 1;
  const cpoScoped = multiCpo && !!scopeCpo && (mode === "dn" || mode === "invoice");
  const lines = (data?.line_items || [])
    .filter((l) => (mode ? lineRemaining(l) > 0 : true))
    .filter((l) => (cpoScoped ? l.client_po_id === scopeCpo : true));
  const whtBase = usePrice ? (data?.line_items || []).reduce((s, l) => s + (Number(qty[l.line_index]) > 0 ? Number(qty[l.line_index]) * (Number(prices[l.line_index]) || 0) : 0), 0) : 0;
  const whtPreview = whtApplicable ? Math.round(whtBase * (Number(whtRate) || 0)) / 100 : 0;

  const runOcr = async (file) => {
    if (!file) return;
    setOcrLoading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("document_type", mode === "sup" ? "Supplier_Tax_Invoice" : "Tax_Receipt");
    try {
      const r = await api.post("/ocr/extract", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const f = r.data?.fields || {};
      if (mode === "sup") {
        if (f.supplier_tax_invoice_no) setSupInvNo(String(f.supplier_tax_invoice_no));
        if (f.supplier_efd_qr_code) setEfdCode(String(f.supplier_efd_qr_code));
        if (f.supplier_efd_time) setEfdTime(String(f.supplier_efd_time));
        const items = f.line_items || [];
        if (items.length) {
          setQty((s) => { const n = { ...s }; lines.forEach((l, i) => { if (items[i]?.qty != null) n[l.line_index] = items[i].qty; }); return n; });
          setPrices((s) => { const n = { ...s }; lines.forEach((l, i) => { if (items[i]?.unit_price != null) n[l.line_index] = items[i].unit_price; }); return n; });
        }
      } else {
        if (f.tax_receipt_code) setEfdCode(String(f.tax_receipt_code));
        if (f.receipt_time || f.date) setEfdTime(String(f.receipt_time || f.date));
      }
      toast.success("Document scanned — fields auto-filled (edit as needed)");
    } catch (e) {
      toast.error(errMsg(e) || "Could not read the document");
    } finally {
      setOcrLoading(false);
    }
  };

  const submit = async () => {
    try {
      const items = (data?.line_items || [])
        .filter((l) => Number(qty[l.line_index]) > 0)
        .map((l) => ({ line_index: l.line_index, qty: Number(qty[l.line_index]),
          description: l.description, sku: l.sku, item_id: l.item_id,
          unit_price: Number(prices[l.line_index]) || 0, tax_type: (mode === "sup" ? (l.supplier_tax_type || l.tax_type) : l.tax_type) }));
      if (items.length === 0) { toast.error("Enter a quantity for at least one line."); return false; }
      if (mode === "grv") {
        if (!signer) { toast.error("Enter the name of the person receiving the goods."); return false; }
        await api.post(`/transactions/${tid}/grv`, { received_by: signer, date: docDate || null, doc_no: docNo || null, notes, items });
        toast.success("Goods received — stock updated");
      } else if (mode === "dn") {
        const r = await api.post(`/transactions/${tid}/delivery-note`, { received_by: signer || null, date: docDate || null, doc_no: docNo || null, notes, client_po_no: clientPo || null, items, fx_rate: Number(fxRate) || null });
        if (r.data?.accrual?.unmatched?.length) toast.warning(`${r.data.accrual.unmatched.length} delivered line(s) had no matching Client PO price — a fallback estimate was used for the unbilled-revenue accrual.`);
        toast.success("Delivery note created — stock reduced");
      } else if (mode === "sup") {
      if (creditDays === "") { toast.error("Credit days are required."); return false; }
        const r = await api.post(`/transactions/${tid}/supplier-invoice`, {
          currency: data?.currency || "TZS", vat_rate: data?.vat_rate ?? 18, date: docDate || null, credit_days: Number(creditDays),
          wht_applicable: whtApplicable, wht_rate: Number(whtRate) || 0, wht_method: whtMethod,
          supplier_invoice_no: supInvNo || null, efd_qr_code: efdCode || null, efd_time: efdTime || null,
          doc_no: docNo || null, notes, items,
        });
        toast.success("Supplier invoice matched & posted to GL");
        const rec = r.data?.cost_reconciliation;
        if (rec?.reason === "period_closed") {
          toast.warning(`Actual cost differs from the PO estimate, but ${rec.blocked_periods?.join(", ")} is locked/closed — AVCO was NOT rewritten. Post a prior-period adjustment instead.`);
        } else if (rec?.applied) {
          toast.success("Inventory AVCO & COGS reconciled to the actual supplier cost.");
        }
      } else {
        const payload = {
          currency: data?.client_currency || data?.currency || "TZS", vat_rate: (data?.vat_rate || 18), date: docDate || null, fx_rate: Number(fxRate) || null,
          wht_applicable: whtApplicable, wht_rate: Number(whtRate) || 0, wht_method: whtMethod,
          client_id: transaction?.client_id || null, client_po_no: clientPo || null,
          credit_days: creditDays !== "" ? Number(creditDays) : null,
          lx_efd_qr_code: efdCode || null, lx_efd_time: efdTime || null,
          bank_account_id: bank !== "none" ? bank : null, doc_no: docNo || null, notes, items,
        };
        if (editInv) {
          await api.put(`/client-invoices/${editInv.id}`, payload);
          toast.success("Client invoice updated — GL re-posted");
        } else {
          await api.post(`/transactions/${tid}/client-invoice`, payload);
          toast.success("Client invoice created & posted to GL");
        }
      }
      setMode(null); setEditInv(null); load(); onChanged?.();
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };

  const del = async (kind, id) => {
    const list = { grv: data?.grvs, dn: data?.delivery_notes }[kind] || [];
    const doc = list.find((x) => x.id === id);
    if (doc && doc.status === "authorized") {
      const reason = window.prompt(`This ${kind === "dn" ? "delivery note" : "GRV"} is authorised, so it can only be reversed (dated reversing entries + stock reversal). Reason for the reversal:`);
      if (!reason || !reason.trim()) return;
      const date = window.prompt("Reversal date (YYYY-MM-DD):", new Date().toISOString().slice(0, 10));
      if (!date) return;
      try {
        const r = await api.post(`/docs/${kind === "dn" ? "delivery_note" : "grv"}/${id}/reverse`, { reason: reason.trim(), date });
        toast.success(r.data.posted ? "Reversed — reversing entries posted" : "Reversal request submitted — awaiting approval");
        load(); onChanged?.();
      } catch (e) { toast.error(errMsg(e)); }
      return;
    }
    if (!window.confirm("Delete this document? Only unposted (pending/draft) documents can be deleted.")) return;
    const url = kind === "grv" ? `/grvs/${id}` : kind === "dn" ? `/delivery-notes/${id}` : kind === "sup" ? `/supplier-invoices/${id}` : `/client-invoices/${id}`;
    try { await api.delete(url); toast.success("Deleted"); load(); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const openReturn = (dir) => {
    setRetMode(dir); setRetQty({});
    const cpos = data?.client_pos || [];
    setRetScopeCpo(dir === "client" && cpos.length === 2 ? cpos[0].id : "");
    setRetMeta({ date: new Date().toISOString().slice(0, 10), reason: "", doc_no: "" });
  };
  const submitReturn = async () => {
    const items = (retData?.line_items || []).filter((l) => Number(retQty[l.line_index]) > 0)
      .map((l) => ({ line_index: l.line_index, qty: Number(retQty[l.line_index]) }));
    if (!items.length) { toast.error("Enter a quantity to return on at least one line."); return; }
    try {
      await api.post(`/transactions/${tid}/goods-return`, { direction: retMode, date: retMeta.date || null, reason: retMeta.reason || "", doc_no: retMeta.doc_no || null, items });
      toast.success(retMode === "client" ? "Goods returned from client — stock in" : "Goods returned to supplier — stock out");
      setRetMode(null); load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const delReturn = async (id) => {
    if (!window.confirm("Delete this goods return? Stock and GL entries will be reversed.")) return;
    try { await api.delete(`/goods-returns/${id}`); toast.success("Goods return deleted"); load(); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return null;
  const cur = data.currency;
  const clientCur = data.client_currency || cur;
  const cpoLinked = (data.line_items || []).some((l) => l.client_po_id);
  const retLines = retData?.line_items || [];
  const retMap = {};
  retLines.forEach((l) => { retMap[l.line_index] = { c: l.qty_client_returned || 0, s: l.qty_supplier_returned || 0 }; });
  const returnableLines = (dir) => retLines.filter((l) => (dir === "client" ? l.client_returnable : l.supplier_returnable) > 0.001 && (dir === "client" && multiCpo && retScopeCpo ? l.client_po_id === retScopeCpo : true));
  const allReturns = [
    ...((retData?.client_returns || []).map((r) => ({ ...r, _dir: "from Client" }))),
    ...((retData?.supplier_returns || []).map((r) => ({ ...r, _dir: "to Supplier" }))),
  ];
  // Delivery Notes support MULTIPLE signed copies (`signed_docs` list); the legacy
  // single `signed_doc` field is still honoured (surfaced with a stable id 'legacy').
  const dnAttachments = (dn) => (dn.signed_docs?.length ? dn.signed_docs : (dn.signed_doc ? [{ ...dn.signed_doc, id: "legacy" }] : []));
  const hasSignedDn = (dn) => dnAttachments(dn).length > 0;
  const dnNotReceived = (data.delivery_notes || []).some((dn) => !hasSignedDn(dn));
  const hasSignedGrv = (grv) => !!grv.signed_doc;

  const removeSignedDn = async (dn, attId) => {
    if (!window.confirm("Remove this signed copy? Other attachments on this DN stay in place.")) return;
    try { await api.delete(`/delivery-notes/${dn.id}/signed-copy/${attId}`); toast.success("Signed copy removed"); load(); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const uploadSigned = (path, okMsg) => async (doc, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try { await api.post(`/${path}/${doc.id}/signed-copy`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(okMsg); load(); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const uploadSignedDn = uploadSigned("delivery-notes", "Signed DN attached");
  const uploadSignedGrv = uploadSigned("grvs", "Signed GRVNI attached");

  const authorizeDoc = (kind) => async (doc) => {
    const path = kind === "grv" ? "grvs" : "delivery-notes";
    const label = kind === "grv" ? "GRV" : "Delivery Note";
    try { await api.post(`/${path}/${doc.id}/authorize`); toast.success(`${label} authorized — posted to the GL`); load(); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const rejectDoc = (kind) => async (doc) => {
    const path = kind === "grv" ? "grvs" : "delivery-notes";
    const reason = window.prompt("Reason for rejection (optional):", "");
    if (reason === null) return;
    try { await api.post(`/${path}/${doc.id}/reject`, { reason }); toast.success("Rejected"); load(); onChanged?.(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const authorizeGrv = authorizeDoc("grv");
  const rejectGrv = rejectDoc("grv");
  const authorizeDn = authorizeDoc("dn");
  const rejectDn = rejectDoc("dn");

  const openResubmit = (kind, doc) => setResubmit({ kind, id: doc.id, no: doc.grv_no || doc.dn_no, note: "",
    items: (doc.items || []).map((it) => ({ line_index: it.line_index, qty: it.qty, description: it.description, sku: it.sku })) });
  const submitResubmit = async () => {
    const path = resubmit.kind === "grv" ? "grvs" : "delivery-notes";
    try {
      await api.post(`/${path}/${resubmit.id}/resubmit`, { note: resubmit.note || "", items: resubmit.items.map((it) => ({ line_index: it.line_index, qty: Number(it.qty) || 0 })) });
      toast.success("Resubmitted for authorization"); setResubmit(null); load(); onChanged?.();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const Tag = ({ tone, testid, children }) => (
    <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-sm border px-2 py-0.5 text-xs font-semibold ${tone === "red" ? "border-red-300 bg-red-50 text-red-700" : "border-amber-300 bg-amber-50 text-amber-700"}`}>{children}</span>
  );

  return (
    <div data-testid="fulfillment-panel">
      <FulfillmentKpis data={data} />
      <div className="mb-2 flex items-center justify-between">
        <span className="lx-label">Fulfillment (GRV / Delivery / Invoicing)</span>
        <span className="flex items-center gap-2 text-sm" data-testid="fulfillment-total-gp">
          {data.gp_estimated && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700" title="Cost of sales for at least one invoiced line is an estimate from the Somcore PO cost (supplier invoice not yet matched)." data-testid="gp-estimated-badge">GP incl. PO estimate</span>}
          Total GP: <span className={`lx-num font-semibold ${data.total_gp >= 0 ? "text-emerald-600" : "text-red-600"}`}>TZS {fmtNum(data.total_gp)}</span>
        </span>
      </div>

      {data.pending_cost_adjustment && (
        <div className="mb-3 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800" data-testid="pending-cost-adjustment">
          <span className="font-semibold">Prior-period cost adjustment needed:</span> a matched supplier invoice differs from the PO estimate, but {(data.pending_cost_adjustment.blocked_periods || []).join(", ")} is locked/closed, so inventory AVCO/COGS were <span className="font-semibold">not</span> auto-corrected. Net cost variance {" "}
          <span className="lx-num font-semibold">{curSym(cur)} {fmtNum((data.pending_cost_adjustment.variances || []).reduce((s, v) => s + (v.variance_tzs || 0), 0))}</span>. Ask Ronick to unlock the period for an auto-rebuild, or post a manual prior-period adjustment.
        </div>
      )}

      {data.cost_reconciliation_log?.length > 0 && (
        <details className="mb-3 rounded-sm border border-gray-200 bg-gray-50 px-3 py-2 text-xs" data-testid="cost-reconciliation-history">
          <summary className="cursor-pointer font-semibold text-gray-700">Cost reconciliation history ({data.cost_reconciliation_log.length})</summary>
          <div className="mt-2 space-y-2">
            {data.cost_reconciliation_log.map((e, idx) => (
              <div key={idx} className="border-t border-gray-100 pt-1">
                <div className="text-gray-500">{(e.at || "").slice(0, 10)} · {e.by || "system"} · {e.note}</div>
                {(e.variances || []).map((v, j) => (
                  <div key={j} className="lx-num flex justify-between gap-4">
                    <span className="truncate">{v.description || v.sku || `Line ${v.line_index}`}</span>
                    <span className="whitespace-nowrap">est {fmtNum(v.estimate_unit_tzs)} → act {v.actual_unit_tzs == null ? "—" : fmtNum(v.actual_unit_tzs)} <span className={v.variance_tzs > 0 ? "text-red-600" : v.variance_tzs < 0 ? "text-emerald-700" : "text-gray-400"}>(total {fmtNum(v.variance_tzs)})</span></span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </details>
      )}

      {data.po_cost_variance_flag && (
        <div className="mb-3 flex items-center gap-2 rounded-sm border border-indigo-300 bg-indigo-50 px-3 py-2 text-sm text-indigo-800" data-testid="po-cost-variance-alert">
          <span className="font-semibold">Cost reconciliation:</span> the actual supplier-invoiced cost differs from the Somcore PO estimate by <span className="lx-num font-semibold">{curSym(cur)} {fmtNum(Math.abs(data.po_cost_variance))}</span> ({data.po_cost_variance > 0 ? "supplier billed more than the PO — GP reduced" : "supplier billed less than the PO — GP improved"}).
        </div>
      )}

      {(data.flag_grvni || data.flag_delivered_not_invoiced || dnNotReceived) && (
        <div className="mb-3 flex flex-wrap gap-2" data-testid="fulfillment-status-tags">
          {data.flag_grvni && <Tag tone="red" testid="tag-grvni">GRVNI — Goods Received, Voucher/Invoice Not Received</Tag>}
          {data.flag_delivered_not_invoiced && <Tag tone="amber" testid="tag-delivered-not-invoiced">Delivered - Not Invoiced</Tag>}
          {dnNotReceived && <Tag tone="red" testid="tag-dn-not-received">Signed DN pending</Tag>}
        </div>
      )}

      {data.cost_variance_flag && (
        <div className="mb-3 flex items-center gap-2 rounded-sm border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="cost-variance-alert">
          <span className="font-semibold">⚠ Cost variance:</span> landed cost of invoiced goods (TSh {fmtNum(data.total_landed_invoiced)}) exceeds client-invoiced revenue (TSh {fmtNum(data.total_client_invoiced)}) by TSh {fmtNum(Math.abs(data.cost_variance))} — gross profit eroded.
        </div>
      )}

      <div className="mb-3 flex flex-wrap gap-2">
        {!transaction?.direct_sale && <Button size="sm" variant="outline" data-testid="grv-btn" onClick={() => openDialog("grv")}><Package size={15} className="mr-1" />Goods Received</Button>}
        {!cpoLinked && <Button size="sm" variant="outline" data-testid="dn-btn" onClick={() => openDialog("dn")}><Truck size={15} className="mr-1" />Deliver Goods</Button>}
        {!transaction?.direct_sale && <Button size="sm" variant="outline" data-testid="sup-invoice-btn" onClick={() => openDialog("sup")}><FileText size={15} className="mr-1" />Match Supplier Invoice</Button>}
        {!cpoLinked && <Button size="sm" variant="outline" data-testid="invoice-client-btn" onClick={() => openDialog("invoice")}><Receipt size={15} className="mr-1" />Invoice Client</Button>}
        {cpoLinked && (data.client_pos || []).map((c) => (
          <a key={c.id} href={`/finance/client-pos/${c.id}`} className="inline-flex items-center rounded-sm border border-[#1B4383]/40 px-2 py-1 text-xs font-semibold text-[#1B4383] hover:bg-[#1B4383]/5" data-testid={`goto-cpo-${c.id}`}>Deliver &amp; Invoice on {c.no}{c.name ? ` · ${c.name}` : ""} →</a>
        ))}
        <Button size="sm" variant="outline" className="border-sky-300 text-sky-700 hover:bg-sky-50" data-testid="return-client-btn" onClick={() => openReturn("client")}><ArrowUUpLeft size={15} className="mr-1" />Return from Client</Button>
        {!transaction?.direct_sale && <Button size="sm" variant="outline" className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="return-supplier-btn" onClick={() => openReturn("supplier")}><ArrowUUpRight size={15} className="mr-1" />Return to Supplier</Button>}
      </div>

      {/* Progress table */}
      <div className="overflow-x-auto rounded-sm border border-gray-200">
        <table className="w-full text-xs" data-testid="fulfillment-progress">
          <thead className="bg-gray-50 lx-label text-gray-500">
            <tr>
              <th className="px-2 py-1.5 text-left">Item</th>
              <th className="px-2 py-1.5 text-right">Ordered</th>
              <th className="px-2 py-1.5 text-right">Received</th>
              <th className="px-2 py-1.5 text-right">Delivered</th>
              <th className="px-2 py-1.5 text-right">Invoiced</th>
              <th className="px-2 py-1.5 text-right">Supplier Invoiced</th>
              <th className="px-2 py-1.5 text-right">Returned (C/S)</th>
              <th className="px-2 py-1.5 text-right">Landed/Unit (TZS)</th>
              <th className="px-2 py-1.5 text-right">GP (TZS)</th>
            </tr>
          </thead>
          <tbody>
            {data.line_items.map((l) => (
              <tr key={l.line_index} className="border-t border-gray-100">
                <td className="px-2 py-1.5">{l.description || "—"}{l.stocking ? " · stock" : (l.client_po_no ? ` · ${l.client_po_no}` : "")}
                  {l.item_type === "Service" && <span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">SERVICE</span>}
                  {l.item_type === "Service" && (l.service_completed
                    ? <span className="ml-1 text-[10px] text-emerald-700" data-testid={`svc-done-${l.line_index}`}>completed {String(l.service_completed_date || "").slice(0, 10)}</span>
                    : <button className="ml-1 rounded-sm border border-[#1B4383]/40 px-1.5 py-0.5 text-[10px] font-semibold text-[#1B4383]" data-testid={`svc-complete-${l.line_index}`} onClick={async () => {
                      const date = window.prompt("Service completion date (YYYY-MM-DD):", new Date().toISOString().slice(0, 10)); if (!date) return;
                      const job = window.prompt("Job card / certificate no. (optional):", "") || null;
                      try { await api.post(`/transactions/${tid}/lines/${l.line_index}/service-complete`, { date, job_card: job }); toast.success("Service marked complete"); load(); } catch (e) { toast.error(errMsg(e)); }
                    }}>Mark service complete</button>)}
                </td>
                <td className="px-2 py-1.5 text-right lx-num">{fmtNum(l.qty_ordered)}</td>
                <td className="px-2 py-1.5 text-right lx-num">{fmtNum(l.qty_received)}</td>
                <td className="px-2 py-1.5 text-right lx-num">{fmtNum(l.qty_delivered)}</td>
                <td className="px-2 py-1.5 text-right lx-num">{fmtNum(l.qty_invoiced)}</td>
                <td className="px-2 py-1.5 text-right lx-num" data-testid={`sup-invoiced-${l.line_index}`}>{fmtNum(l.qty_supplier_invoiced)}</td>
                <td className="px-2 py-1.5 text-right lx-num text-gray-500" data-testid={`returned-${l.line_index}`}>{fmtNum(retMap[l.line_index]?.c || 0)} / {fmtNum(retMap[l.line_index]?.s || 0)}</td>
                <td className="px-2 py-1.5 text-right lx-num text-gray-500" data-testid={`landed-unit-${l.line_index}`}>
                  {fmtNum(l.landed_unit_cost)}
                  {l.cost_basis === "actual"
                    ? <span className="ml-1 rounded-sm bg-emerald-50 px-1 text-[9px] font-semibold text-emerald-700" title="Cost of sales = actual supplier-invoiced cost">actual</span>
                    : l.cost_basis === "mixed"
                      ? <span className="ml-1 rounded-sm bg-sky-50 px-1 text-[9px] font-semibold text-sky-700" title="Part actual supplier cost, part Somcore PO estimate (supplier invoice partially matched)">part actual</span>
                    : l.cost_basis === "estimate"
                      ? <span className="ml-1 rounded-sm bg-amber-50 px-1 text-[9px] font-semibold text-amber-700" title="Cost of sales estimated from the Somcore PO cost — supplier invoice not yet matched">PO est</span>
                      : null}
                </td>
                <td className={`px-2 py-1.5 text-right lx-num ${l.gp >= 0 ? "text-emerald-600" : "text-red-600"}`} data-testid={`gp-${l.line_index}`}>{fmtNum(l.gp)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Supplier invoice match */}
      {match && match.received_value_incl > 0 && (
        <div className="mt-3 rounded-sm border border-gray-200 bg-gray-50/60 p-3" data-testid="supplier-match">
          <div className="lx-label mb-1 text-gray-500">Supplier Invoice Match</div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
            <span>Goods received (incl. VAT): <span className="lx-num font-medium">{curSym(cur)} {fmtNum(match.received_value_incl)}</span></span>
            <span>Supplier invoiced (matched{match.invoice_count ? ` · ${match.invoice_count}` : ""}): <span className="lx-num font-medium">{curSym(cur)} {fmtNum(match.supplier_invoice_amount)}</span></span>
            <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${match.flag === "Matched" ? "bg-emerald-100 text-emerald-700" : match.flag === "Over-billed" ? "bg-red-100 text-red-700" : match.flag === "Awaiting Supplier Invoice" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`} data-testid="match-flag">
              {match.flag}{(match.flag !== "Matched" && match.flag !== "Awaiting Supplier Invoice") ? ` · ${cur} ${fmtNum(Math.abs(match.variance))}` : ""}
            </span>
            {match.flag === "Awaiting Supplier Invoice" && (
              <span className="text-xs text-gray-400">Expected payable {curSym(cur)} {fmtNum(match.expected_payable)} — match the supplier's tax invoice to reconcile.</span>
            )}
          </div>
        </div>
      )}

      {/* Unbilled AR accrual */}
      <UnbilledARPanel tid={tid} />
      {!isImport && <LocalCharges tid={tid} onChanged={() => { load(); onChanged?.(); }} />}

      {/* Document lists */}
      <DocList title="Goods Received Vouchers" rows={data.grvs} noLabel="grv_no" signer={(d) => d.received_by} pdf="grv" onDel={(id) => del("grv", id)} testid="grv-list"
        signAttach hasSigned={hasSignedGrv} onUploadSigned={uploadSignedGrv} signLabel="GRVNI"
        authFlow canAuthorize={canAuthorizeFulfillment} onAuthorize={authorizeGrv} onReject={rejectGrv} authKind="grv" onResubmit={(d) => openResubmit("grv", d)} currentUserName={user?.name} />
      <DocList title="Delivery Notes" rows={data.delivery_notes} noLabel="dn_no" signer={(d) => d.received_by} pdf="delivery-note-doc" onDel={(id) => del("dn", id)} testid="dn-list"
        signAttach multi attachments={dnAttachments} onUploadSigned={uploadSignedDn} onRemoveSigned={removeSignedDn} signLabel="DN"
        authFlow canAuthorize={canAuthorizeFulfillment} onAuthorize={authorizeDn} onReject={rejectDn} authKind="dn" onResubmit={(d) => openResubmit("dn", d)} currentUserName={user?.name} />
      <InvoiceList title="Supplier Invoices" rows={data.supplier_invoices} noLabel="invoice_no" cur={cur} pdf={null} onDel={(id) => del("sup", id)} onPayments={setPayView} testid="sup-inv-list" extraRef={(d) => d.supplier_invoice_no} draftAware onEditDraft={openEditSupplier} onPostDraft={postDraft} />
      <InvoiceList onSetFx={user?.is_owner ? setFxFor : null} title="Client Invoices" rows={data.client_invoices} noLabel="invoice_no" cur={clientCur} pdf="client-invoice" onDel={(id) => del("invoice", id)} onPayments={setPayView} testid="inv-list" draftAware onEditDraft={openEditInvoice} onPostDraft={postClientDraft} reverseEnabled onReversed={onChanged} />

      {allReturns.length > 0 && (
        <div className="mt-3" data-testid="returns-list">
          <div className="lx-label mb-1 text-gray-500">Goods Returned Notes ({allReturns.length})</div>
          <div className="space-y-1">
            {allReturns.map((d) => (
              <div key={d.id} className="flex items-center justify-between rounded-sm border border-gray-100 px-2 py-1.5 text-sm">
                <span className="lx-num font-medium">{d.return_no} <span className="text-xs text-gray-400">· {d._dir}</span></span>
                <span className="text-xs text-gray-500">{fmtDate(d.date)}{d.reason ? ` · ${d.reason}` : ""}</span>
                <span className="flex items-center gap-2">
                  <PrintButton path={`pdf/goods-return/${d.id}`} testid={`dl-return-${d.return_no}`} className="text-gray-400 hover:text-[#1B4383]"><DownloadSimple size={16} /></PrintButton>
                  <button className="text-gray-400 hover:text-red-500" data-testid={`del-return-${d.return_no}`} onClick={() => delReturn(d.id)}><Trash size={15} /></button>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Credit & Debit Notes — consolidated into the PO card */}
      <CreditDebitNotes tid={tid} summary={retData} onChanged={() => { load(); onChanged?.(); }} />

      {/* Create dialog */}
      <Dialog open={!!mode} onOpenChange={(v) => { if (!v) { setMode(null); setEditInv(null); } }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="fulfillment-dialog">
          <DialogHeader><DialogTitle className="font-display">
            {mode === "grv" ? "Goods Received (GRV)" : mode === "dn" ? "Deliver Goods (Delivery Note)" : mode === "sup" ? (editSup ? "Edit Supplier Invoice Draft" : "Match Supplier Invoice") : (editInv ? `Edit Client Invoice ${editInv.invoice_no}` : "Invoice Client (itemized)")}
          </DialogTitle>
          <DialogDescription>{editInv ? "This invoice is still a draft — amend the quantities/prices below, then Save Draft or Confirm & Post to GL. Nothing is posted until you confirm." : `Enter quantities for the lines below. You can only ${mode === "grv" ? "receive up to what was ordered" : mode === "dn" ? (transaction?.direct_sale ? "deliver up to what was ordered (drawn from stock)" : "deliver up to what was received") : mode === "sup" ? "invoice up to what was received" : "invoice up to what was delivered"}.`}</DialogDescription>
          </DialogHeader>

          {(mode === "dn" || mode === "invoice") && multiCpo && (
            <div className="rounded-sm border border-sky-200 bg-sky-50/60 p-3" data-testid="cpo-scope-block">
              <Label className="lx-label text-sky-700">This Somcore PO covers {poOptions.length} Client POs — choose which one this {mode === "dn" ? "delivery" : "invoice"} applies to</Label>
              <Select value={scopeCpo || "all"} onValueChange={(v) => { const val = v === "all" ? "" : v; setScopeCpo(val); const po = poOptions.find((p) => p.id === val); setClientPo(po?.no || ""); }}>
                <SelectTrigger className="mt-1" data-testid="cpo-scope-select"><SelectValue placeholder="Select Client PO" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Client POs</SelectItem>
                  {poOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.no}{p.name ? ` · ${p.name}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {lines.length === 0 && <div className="text-sm text-gray-500">Nothing remaining to {mode === "grv" ? "receive" : mode === "dn" ? "deliver" : mode === "sup" ? "match. Receive goods (GRV) first" : "invoice. Deliver goods first"}{cpoScoped ? " for the selected Client PO" : ""}.</div>}

          {lines.length > 0 && (
            <div className="space-y-2">
              {mode === "sup" && (
                <div className="grid grid-cols-3 gap-3 rounded-sm border border-amber-200 bg-amber-50/40 p-3">
                  <div className="col-span-3 flex items-center justify-between">
                    <span className="lx-label text-amber-700">{isImport ? "Supplier Invoice (import — no EFD)" : "Supplier Tax Invoice (EFD)"}</span>
                    <OcrUpload onFile={runOcr} loading={ocrLoading} testid="sup-ocr-upload" label="Scan invoice (OCR)" />
                  </div>
                  <Field label="Supplier Invoice No"><Input data-testid="sup-inv-no" value={supInvNo} onChange={(e) => setSupInvNo(e.target.value)} /></Field>
                  {!isImport && <Field label="EFD Verification Code"><Input data-testid="sup-efd-code" value={efdCode} onChange={(e) => setEfdCode(e.target.value)} /></Field>}
                  {!isImport && <Field label="EFD Time (24h HH:MM:SS)"><EfdTimeField testid="sup-efd-time" value={efdTime} onChange={setEfdTime} /></Field>}
                </div>
              )}
              <div className="grid grid-cols-12 gap-2 lx-label text-gray-500">
                <div className="col-span-6">Item</div>
                <div className="col-span-2 text-right">Remaining</div>
                <div className="col-span-2 text-right">{usePrice ? "Unit Price" : "Qty"}</div>
                <div className="col-span-2 text-right">{usePrice ? "Qty" : ""}</div>
              </div>
              {lines.map((l) => (
                <div key={l.line_index} className="grid grid-cols-12 items-center gap-2" data-testid={`ful-line-${l.line_index}`}>
                  <div className="col-span-6 text-sm">{l.description || "—"} {l.sku ? <span className="text-xs text-gray-400">· {l.sku}</span> : null}</div>
                  <div className="col-span-2 text-right lx-num text-sm text-gray-500">{fmtNum(lineRemaining(l))}</div>
                  {usePrice && (
                    <div className="col-span-2">
                      <Input type="number" className="h-8 text-right text-sm" data-testid={`ful-price-${l.line_index}`} disabled={mode === "invoice" && !editInv && l.client_po_price != null} title={(mode === "invoice" && l.client_po_price != null) ? "Selling price from the linked Client PO" : undefined} value={prices[l.line_index] ?? ""} onChange={(e) => setPrices((s) => ({ ...s, [l.line_index]: e.target.value }))} />
                    </div>
                  )}
                  <div className="col-span-2">
                    <Input type="number" className="h-8 text-right text-sm" placeholder="0" max={l[remainKey]} data-testid={`ful-qty-${l.line_index}`} value={qty[l.line_index] ?? ""} onChange={(e) => setQty((s) => ({ ...s, [l.line_index]: e.target.value }))} />
                  </div>
                </div>
              ))}

              {(mode === "grv" || mode === "dn") && (
                <Field label={mode === "grv" ? "Received by (typed name)" : "Received by (optional — recipient signs the printout)"}>
                  <Input data-testid="ful-signer" value={signer} onChange={(e) => setSigner(e.target.value)} placeholder={mode === "dn" ? "Leave blank for recipient to sign" : "Full name"} />
                </Field>
              )}
              <Field label={mode === "grv" ? "Goods received date" : mode === "dn" ? "Delivery date" : "Invoice date"}>
                <DateField data-testid="ful-date" value={docDate} onChange={(e) => setDocDate(e.target.value)} />
              </Field>
              {mode === "sup" && (
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Credit Days (payment terms) *"><Input type="number" data-testid="sup-credit-days" value={creditDays} onChange={(e) => setCreditDays(e.target.value)} placeholder="e.g. 30" /></Field>
                  <Field label="GL Posting Date (when it hits the ledger)"><DateField data-testid="sup-gl-date" value={glDate} onChange={(e) => setGlDate(e.target.value)} /></Field>
                </div>
              )}
              {editInv && mode === "invoice" ? (
                <Field label="Client Invoice No"><Input data-testid="ful-doc-no" value={docNo} disabled className="disabled:opacity-100 font-medium text-gray-700" /></Field>
              ) : (
                <DocNumberField
                  docKey={{ grv: "grv", dn: "delivery_note", invoice: "client_invoice", sup: "supplier_invoice" }[mode]}
                  label={mode === "grv" ? "GRV No" : mode === "dn" ? "Delivery Note No" : mode === "sup" ? "Supplier Invoice (internal) No" : "Client Invoice No"}
                  testid="ful-doc-no" value={docNo} onChange={setDocNo} />
              )}
              {mode === "dn" && (
                <>
                  <Field label="Client PO No (optional)"><Input data-testid="ful-clientpo" value={clientPo} onChange={(e) => setClientPo(e.target.value)} /></Field>
                  <FxRateField currency={data?.client_currency || data?.currency} value={fxRate} onChange={setFxRate} testid="ful-dn-fx" />
                </>
              )}
              {mode === "invoice" && (
                <div className="grid grid-cols-2 gap-3 rounded-sm border border-gray-200 bg-gray-50/40 p-3">
                  <div className="col-span-2 flex items-center justify-between">
                    <span className="lx-label text-gray-500">SOMCORE Tax Invoice (EFD)</span>
                    <OcrUpload onFile={runOcr} loading={ocrLoading} testid="inv-ocr-upload" label="Scan EFD receipt (OCR)" />
                  </div>
                  <Field label="Client PO No">
                    <Input data-testid="ful-inv-clientpo" list="client-po-list" placeholder="Select or type" value={clientPo} onChange={(e) => setClientPo(e.target.value)} />
                    <datalist id="client-po-list">
                      {clientPos.map((p) => <option key={p.id} value={p.client_po_no}>{p.client_name || ""}</option>)}
                    </datalist>
                  </Field>
                  <Field label="Credit Days (for due date) *">
                    <Input type="number" data-testid="ful-inv-credit-days" placeholder="e.g. 30" value={creditDays} onChange={(e) => setCreditDays(e.target.value)} />
                  </Field>
                  <Field label="Bank (for banking details)">
                    <Select value={bank} onValueChange={setBank}>
                      <SelectTrigger data-testid="ful-inv-bank"><SelectValue placeholder="Bank" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">— None —</SelectItem>
                        {banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.bank_name} · {b.currency}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="EFD Verification Code"><Input data-testid="ful-inv-efd-code" value={efdCode} onChange={(e) => setEfdCode(e.target.value)} /></Field>
                  <Field label="EFD Time (24h HH:MM:SS)"><EfdTimeField testid="ful-inv-efd-time" value={efdTime} onChange={setEfdTime} /></Field>
                  <div className="col-span-2"><FxRateField currency={data?.client_currency || data?.currency} value={fxRate} onChange={setFxRate} testid="ful-inv-fx" amount={Object.entries(qty).reduce((s, [k, v]) => s + (Number(v) || 0) * (Number(prices[k]) || 0), 0)} /></div>
                </div>
              )}
              {usePrice && (
                <div className="grid grid-cols-2 gap-3 rounded-sm border border-indigo-200 bg-indigo-50/40 p-3">
                  <div className="col-span-2 flex items-center gap-2">
                    <input type="checkbox" id="wht-toggle" data-testid="ful-wht-toggle" checked={whtApplicable} onChange={(e) => setWhtApplicable(e.target.checked)} />
                    <label htmlFor="wht-toggle" className="lx-label text-indigo-700 cursor-pointer">
                      Withholding Tax (WHT) applicable {mode === "invoice" ? "— client withholds our tax (receivable)" : "— we withhold supplier's tax (payable)"}
                    </label>
                  </div>
                  {whtApplicable && (
                    <>
                      <Field label="WHT Rate (%)">
                        <Input type="number" data-testid="ful-wht-rate" value={whtRate} onChange={(e) => setWhtRate(e.target.value)} />
                      </Field>
                      {mode !== "invoice" && (
                        <Field label="WHT method">
                          <select className="h-9 w-full rounded-sm border border-gray-200 bg-white px-2 text-sm" data-testid="ful-wht-method" value={whtMethod} onChange={(e) => setWhtMethod(e.target.value)}>
                            <option value="net_down">Net down — pay total − WHT</option>
                            <option value="gross_up">Gross up — pay full total, Somcore bears WHT</option>
                          </select>
                        </Field>
                      )}
                      <Field label="WHT Amount (on net-of-VAT)">
                        <div className="lx-num text-sm pt-2 text-indigo-700" data-testid="ful-wht-amount">
                          {curSym(data?.currency || "TZS")} {fmtNum(whtPreview)}
                        </div>
                        {mode !== "invoice" && <div className="text-[11px] text-gray-500" data-testid="ful-wht-preview">Gross {fmtNum(whtBase * (1 + (Number(data?.vat_rate ?? 18) || 0) / 100))} · WHT {fmtNum(whtPreview)} · Cash payable {fmtNum(whtBase * (1 + (Number(data?.vat_rate ?? 18) || 0) / 100) - (whtMethod === "gross_up" ? 0 : whtPreview))}</div>}
                      </Field>
                    </>
                  )}
                </div>
              )}
              <Field label={mode === "invoice" ? "Notes (printed on the client invoice)" : "Notes"}>
                <Textarea rows={2} data-testid="ful-notes" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder={mode === "invoice" ? "These notes will appear on the printed invoice / PDF." : undefined} />
                {mode === "invoice" && <p className="mt-1 text-[11px] text-gray-500">These notes will appear on the printed invoice / PDF sent to the client.</p>}
              </Field>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => { setMode(null); setEditInv(null); setEditSup(null); }}>Cancel</Button>
            {lines.length > 0 && <Button variant="outline" data-testid="ful-preview" onClick={() => setPreview(true)}>Preview</Button>}
            {lines.length > 0 && (mode === "sup" ? (
              <>
                <Button variant="outline" data-testid="ful-save-draft" onClick={() => saveSup(true)}>{editSup ? "Save Draft" : "Save as Draft"}</Button>
                <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="ful-confirm-post" onClick={() => saveSup(false)}>Confirm &amp; Post to GL</Button>
              </>
            ) : mode === "invoice" ? (
              <>
                <Button variant="outline" data-testid="ful-save-draft" onClick={() => saveClient(true)}>{editInv ? "Save Draft" : "Save as Draft"}</Button>
                <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="ful-confirm-post" onClick={() => saveClient(false)}>Confirm &amp; Post to GL</Button>
              </>
            ) : (
              <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="ful-submit" onClick={submit}>Save</Button>
            ))}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {mode && (() => {
        const sel = (data?.line_items || []).filter((l) => Number(qty[l.line_index]) > 0);
        const priced = usePrice;
        const excl = sel.reduce((s, l) => s + Number(qty[l.line_index]) * (Number(prices[l.line_index]) || 0), 0);
        const taxOf = (l) => ((mode === "sup" ? (l.supplier_tax_type || l.tax_type) : l.tax_type) || "Vatable");
        const _isVatable = (l) => (taxOf(l) === "Vatable");
        const _effRate = sel.some(_isVatable) ? (data?.vat_rate ?? 18) : 0;
        const vat = sel.reduce((s, l) => s + (_isVatable(l) ? Number(qty[l.line_index]) * (Number(prices[l.line_index]) || 0) * (_effRate / 100) : 0), 0);
        const pCur = mode === "invoice" ? clientCur : cur;
        const title = { grv: "Goods Received (GRV)", dn: "Delivery Note", sup: "Supplier Invoice", invoice: "Client Invoice" }[mode];
        const meta = mode === "grv" ? [["GRV No", docNo || "(auto)"], ["Received by", signer], ["Date", docDate]]
          : mode === "dn" ? [["Delivery Note No", docNo || "(auto)"], ["Received by", signer || "(recipient signs)"], ["Client PO", clientPo], ["Date", docDate]]
          : mode === "sup" ? [["Supplier Invoice No", supInvNo], ["Internal No", docNo || "(auto)"], ["EFD Code", efdCode], ["Currency", cur], ["Date", docDate]]
          : [["Invoice No", docNo || "(auto)"], ["Client PO", clientPo], ["Currency", clientCur], ["VAT Rate", `${_effRate}%`], ["Date", docDate]];
        const columns = priced
          ? [{ key: "item", label: "Item" }, { key: "qty", label: "Qty", align: "right" }, { key: "price", label: "Unit Price", align: "right" }, ...(mode === "invoice" ? [{ key: "vat", label: "VAT" }] : []), { key: "amt", label: "Amount", align: "right" }]
          : [{ key: "item", label: "Item" }, { key: "qty", label: "Qty", align: "right" }];
        const vatBadge = (l) => {
          const tt = taxOf(l);
          const isV = tt === "Vatable";
          const label = isV ? `Vatable ${_effRate}%` : (tt === "Zero" ? "Zero 0%" : tt === "Exempt" ? "Exempt" : `${tt} 0%`);
          return <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${isV ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{label}</span>;
        };
        const rows = sel.map((l) => {
          const q = Number(qty[l.line_index]); const p = Number(prices[l.line_index]) || 0;
          const base = priced ? { item: l.description || l.sku || "—", qty: fmtNum(q), price: fmtNum(p, pCur), amt: fmtNum(q * p, pCur) } : { item: l.description || l.sku || "—", qty: fmtNum(q) };
          if (priced && mode === "invoice") base.vat = vatBadge(l);
          return base;
        });
        const totals = priced
          ? [["Exclusive", fmtNum(excl, pCur)], ["VAT", fmtNum(vat, pCur)], ...(whtApplicable ? [["WHT", `-${fmtNum(whtPreview, pCur)}`]] : []), ["Total", fmtNum(excl + vat, pCur), true]]
          : [["Total Qty", fmtNum(sel.reduce((s, l) => s + Number(qty[l.line_index]), 0)), true]];
        return (
          <DocumentPreview
            open={preview} onOpenChange={setPreview} title={title}
            onConfirm={async () => { const ok = mode === "sup" ? await saveSup(false) : mode === "invoice" ? await saveClient(false) : await submit(); if (ok) setPreview(false); }}
            confirmLabel={(mode === "sup" || mode === "invoice") ? "Confirm & Post" : "Confirm & Save"}
            meta={meta} columns={columns} rows={rows} totals={totals}
          />
        );
      })()}

      {/* Payments dialog */}
      <Dialog open={!!payView} onOpenChange={(v) => { if (!v) setPayView(null); }}>
        <DialogContent className="max-w-lg" data-testid="invoice-payments-dialog">
          <DialogHeader><DialogTitle className="font-display">Payments applied — {payView?.ref}</DialogTitle></DialogHeader>
          {(!payView?.payments || payView.payments.length === 0) ? (
            <div className="text-sm text-gray-500">No payments allocated to this invoice yet. Allocate one in the Allocations module.</div>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="lx-label border-b border-gray-100 text-gray-400">
                <th className="py-1 text-left">Reference</th><th className="py-1 text-left">Date</th><th className="py-1 text-right">Amount</th>
              </tr></thead>
              <tbody>
                {payView.payments.map((p) => (
                  <tr key={p.allocation_id} className="border-b border-gray-50">
                    <td className="py-1">{p.ref}</td>
                    <td className="py-1 lx-num">{fmtDate(p.date)}</td>
                    <td className="py-1 text-right lx-num">{fmtNum(p.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setPayView(null)}>Close</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resubmit} onOpenChange={(v) => { if (!v) setResubmit(null); }}>
        <DialogContent className="max-w-lg" data-testid="resubmit-dialog">
          <DialogHeader><DialogTitle className="font-display">Edit &amp; resubmit {resubmit?.kind === "grv" ? "GRV" : "Delivery Note"} {resubmit?.no}</DialogTitle>
          <DialogDescription>Adjust the quantities and resubmit for authorization by Finance or Ronick.</DialogDescription></DialogHeader>
          <div className="space-y-2">
            {(resubmit?.items || []).map((it, idx) => (
              <div key={idx} className="flex items-center gap-2 text-sm" data-testid={`resubmit-item-${idx}`}>
                <span className="flex-1 truncate">{it.description || it.sku || `Line ${it.line_index}`}</span>
                <input type="number" min="0" className="w-24 rounded border border-gray-200 px-2 py-1 text-xs lx-num" value={it.qty}
                  onChange={(e) => setResubmit((r) => ({ ...r, items: r.items.map((x, j) => (j === idx ? { ...x, qty: e.target.value } : x)) }))}
                  data-testid={`resubmit-qty-${idx}`} />
              </div>
            ))}
          </div>
          <div className="mt-2">
            <label className="lx-label text-gray-500">Note (what changed)</label>
            <textarea className="mt-1 w-full rounded border border-gray-200 px-2 py-1 text-sm" rows={2} placeholder="e.g. corrected received qty from 8 to 2"
              value={resubmit?.note || ""} onChange={(e) => setResubmit((r) => ({ ...r, note: e.target.value }))} data-testid="resubmit-note" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResubmit(null)}>Cancel</Button>
            <Button data-testid="resubmit-submit" onClick={submitResubmit}>Resubmit for authorization</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!retMode} onOpenChange={(v) => { if (!v) setRetMode(null); }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="return-dialog">
          <DialogHeader><DialogTitle className="font-display">{retMode === "client" ? "Return Goods from Client (stock in)" : "Return Goods to Supplier (stock out)"}</DialogTitle></DialogHeader>
          {retMode === "client" && multiCpo && (
            <div className="rounded-sm border border-sky-200 bg-sky-50/60 p-3" data-testid="ret-cpo-scope-block">
              <Label className="lx-label text-sky-700">This Somcore PO covers {poOptions.length} Client POs — choose which one this return applies to</Label>
              <Select value={retScopeCpo || "all"} onValueChange={(v) => setRetScopeCpo(v === "all" ? "" : v)}>
                <SelectTrigger className="mt-1" data-testid="ret-cpo-scope-select"><SelectValue placeholder="Select Client PO" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Client POs</SelectItem>
                  {poOptions.map((p) => <SelectItem key={p.id} value={p.id}>{p.no}{p.name ? ` · ${p.name}` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {returnableLines(retMode).length === 0 ? (
            <p className="py-6 text-center text-sm text-gray-400">{retMode === "client" && multiCpo && retScopeCpo ? "No returnable lines for the selected Client PO — pick another or choose 'All Client POs'." : `Nothing available to return ${retMode === "client" ? "from the client (deliver goods first)." : "to the supplier (receive goods first)."}`}</p>
          ) : (
            <div className="mt-3 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Date"><DateField data-testid="ret-date" value={retMeta.date || ""} onChange={(e) => setRetMeta({ ...retMeta, date: e.target.value })} /></Field>
                <DocNumberField docKey={retMode === "client" ? "client_return" : "supplier_return"} label="Return Note No" testid="ret-doc-no" value={retMeta.doc_no} onChange={(v) => setRetMeta({ ...retMeta, doc_no: v })} />
              </div>
              <Field label="Reason"><Input data-testid="ret-reason" value={retMeta.reason || ""} onChange={(e) => setRetMeta({ ...retMeta, reason: e.target.value })} placeholder="e.g. damaged / wrong item" /></Field>
              <div className="overflow-hidden rounded-sm border border-gray-200">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200"><th className="px-2 py-1.5 text-left">Item</th><th className="px-2 py-1.5 text-right">Returnable</th><th className="px-2 py-1.5 text-right">Return Qty</th></tr></thead>
                  <tbody>
                    {returnableLines(retMode).map((l) => {
                      const cap = retMode === "client" ? l.client_returnable : l.supplier_returnable;
                      return (
                        <tr key={l.line_index} className="border-b border-gray-100">
                          <td className="px-2 py-1.5">{l.description}</td>
                          <td className="px-2 py-1.5 text-right lx-num text-gray-500">{fmtNum(cap)}</td>
                          <td className="px-2 py-1.5 text-right"><Input type="number" min={0} max={cap} className="h-7 w-24 text-right text-xs" data-testid={`ret-qty-${l.line_index}`} value={retQty[l.line_index] ?? ""} onChange={(e) => setRetQty({ ...retQty, [l.line_index]: e.target.value })} /></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRetMode(null)}>Cancel</Button>
            {returnableLines(retMode).length > 0 && <Button className="bg-[#1B4383] hover:bg-[#122E5C]" data-testid="submit-return" onClick={submitReturn}>Record Return</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function payBadgeClass(status) {
  return status === "Paid" ? "bg-emerald-100 text-emerald-700" : status === "Partially Paid" ? "bg-amber-100 text-amber-700" : "bg-gray-100 text-gray-500";
}

function DocList({ title, rows, noLabel, signer, pdf, onDel, testid, signAttach, hasSigned, onUploadSigned, onRemoveSigned, multi, attachments, signLabel = "DN", authFlow, canAuthorize, onAuthorize, onReject, authKind = "grv", onResubmit, currentUserName }) {
  if (!rows || rows.length === 0) return null;
  const statusOf = (d) => d.status || "authorized";
  return (
    <div className="mt-3" data-testid={testid}>
      <div className="lx-label mb-1 text-gray-500">{title} ({rows.length})</div>
      <div className="space-y-1">
        {rows.map((d) => {
          const atts = multi ? (attachments ? attachments(d) : []) : [];
          const signed = signAttach ? (multi ? atts.length > 0 : hasSigned(d)) : true;
          const st = statusOf(d);
          return (
            <div key={d.id} className={`rounded-sm border px-2 py-1.5 text-sm ${st === "pending" ? "border-amber-200 bg-amber-50/40" : st === "rejected" ? "border-red-200 bg-red-50/40" : "border-gray-100"}`}>
              <div className="flex items-center justify-between">
                <span className="lx-num font-medium">{d[noLabel]}</span>
                <span className="text-xs text-gray-500">{fmtDate(d.date)} · {signer(d)}</span>
                <span className="flex items-center gap-2">
                  {authFlow && st === "pending" && (
                    <>
                      <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700" title={d.resubmit_note ? `Resubmitted — ${d.resubmit_note}` : ""} data-testid={`status-pending-${d[noLabel]}`}>Pending authorization{d.resubmit_note ? " ✎" : ""}</span>
                      {canAuthorize && (
                        <>
                          <button className="rounded-sm bg-emerald-600 px-2 py-0.5 text-[10px] font-semibold text-white hover:bg-emerald-700" data-testid={`authorize-${authKind}-${d[noLabel]}`} onClick={() => onAuthorize(d)}>Authorize</button>
                          <button className="rounded-sm border border-red-300 bg-white px-2 py-0.5 text-[10px] font-semibold text-red-600 hover:bg-red-50" data-testid={`reject-${authKind}-${d[noLabel]}`} onClick={() => onReject(d)}>Reject</button>
                        </>
                      )}
                    </>
                  )}
                  {authFlow && st === "authorized" && d.authorized_by && (
                    <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700" title={`Authorized by ${d.authorized_by}${d.authorized_at ? " · " + fmtDate(d.authorized_at) : ""}`} data-testid={`status-authorized-${d[noLabel]}`}>Authorized · {d.authorized_by}</span>
                  )}
                  {authFlow && st === "rejected" && (
                    <>
                      <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700" title={d.reject_reason || ""} data-testid={`status-rejected-${d[noLabel]}`}>Rejected{d.rejected_by ? ` · ${d.rejected_by}` : ""}</span>
                      {onResubmit && (canAuthorize || currentUserName === d.created_by) && (
                        <button className="rounded-sm border border-[#1B4383] bg-white px-2 py-0.5 text-[10px] font-semibold text-[#1B4383] hover:bg-[#1B4383]/10" data-testid={`resubmit-${authKind}-${d[noLabel]}`} onClick={() => onResubmit(d)}>Edit &amp; Resubmit</button>
                      )}
                    </>
                  )}
                  {signAttach && !multi && (signed ? (
                    <span className="rounded-sm border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid={`signed-${signLabel.toLowerCase()}-ok-${d[noLabel]}`}>Signed {signLabel} ✓</span>
                  ) : (
                    <label className="inline-flex cursor-pointer items-center gap-1 rounded-sm border border-red-300 bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-600" data-testid={`upload-signed-${signLabel.toLowerCase()}-${d[noLabel]}`}>
                      Upload Signed {signLabel}
                      <input type="file" className="hidden" accept="image/*,application/pdf" onChange={(e) => { onUploadSigned(d, e.target.files[0]); e.target.value = ""; }} />
                    </label>
                  ))}
                  {signAttach && multi && (
                    <>
                      {signed
                        ? <span className="rounded-sm border border-emerald-300 bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid={`signed-${signLabel.toLowerCase()}-count-${d[noLabel]}`}>{atts.length} signed {signLabel} ✓</span>
                        : <span className="rounded-sm border border-red-300 bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-600" data-testid={`no-signed-${signLabel.toLowerCase()}-${d[noLabel]}`}>No signed {signLabel}</span>}
                      <label className="inline-flex cursor-pointer items-center gap-1 rounded-sm border border-[#1B4383] bg-[#E6ECF5] px-1.5 py-0.5 text-[10px] font-semibold text-[#1B4383] hover:bg-[#C9D6EA]" data-testid={`add-signed-${signLabel.toLowerCase()}-${d[noLabel]}`}>
                        + Add signed
                        <input type="file" className="hidden" accept="image/*,application/pdf" onChange={(e) => { onUploadSigned(d, e.target.files[0]); e.target.value = ""; }} />
                      </label>
                    </>
                  )}
                  {pdf && <PrintButton path={`pdf/${pdf}/${d.id}`} testid={`dl-${d[noLabel]}`} className="text-gray-400 hover:text-[#1B4383]"><DownloadSimple size={16} /></PrintButton>}
                  <button className="text-gray-400 hover:text-red-500" data-testid={`del-${d[noLabel]}`} onClick={() => onDel(d.id)}><Trash size={15} /></button>
                </span>
              </div>
              {d.resubmit_history?.length > 0 && (
                <div className="mt-1 space-y-0.5 border-t border-gray-100 pt-1 text-[10px]" data-testid={`resubmit-history-${d[noLabel]}`}>
                  {d.resubmit_history.map((h, i) => (
                    <div key={i} className="flex items-center gap-1.5 text-gray-400">
                      <span className={`rounded-sm px-1 font-semibold ${h.action === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{h.action === "rejected" ? "Rejected" : "Resubmitted"}</span>
                      <span className="text-gray-500">{h.by}</span>
                      {h.at && <span>{fmtDate(h.at)}</span>}
                      {h.note && <span className="min-w-0 flex-1 truncate italic">— {h.note}</span>}
                    </div>
                  ))}
                </div>
              )}
              {signAttach && multi && atts.length > 0 && (                <div className="mt-1.5 flex flex-col gap-1 border-t border-gray-100 pt-1.5" data-testid={`signed-list-${d[noLabel]}`}>
                  {atts.map((a) => (
                    <div key={a.id} className="flex items-center justify-between gap-2 pl-1 text-xs" data-testid={`signed-att-${d[noLabel]}-${a.id}`}>
                      <a href={fileUrl(`delivery-notes/${d.id}/signed-copy/${a.id}/file`)} target="_blank" rel="noreferrer" className="flex min-w-0 items-center gap-1 text-[#1B4383] hover:underline" data-testid={`view-signed-${d[noLabel]}-${a.id}`}>
                        <DownloadSimple size={13} /><span className="truncate">{a.filename || "signed copy"}</span>
                      </a>
                      <span className="flex items-center gap-2 whitespace-nowrap text-gray-400">
                        <span>{a.uploaded_by || "—"}{a.uploaded_at ? ` · ${fmtDate(a.uploaded_at)}` : ""}</span>
                        <button className="hover:text-red-500" title="Remove this signed copy" data-testid={`remove-signed-${d[noLabel]}-${a.id}`} onClick={() => onRemoveSigned(d, a.id)}><Trash size={13} /></button>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function InvoiceList({ title, rows, noLabel, cur, pdf, onDel, onPayments, onEdit, testid, extraRef, draftAware, onEditDraft, onPostDraft, reverseEnabled, onReversed, onSetFx }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div className="mt-3" data-testid={testid}>
      <div className="lx-label mb-1 text-gray-500">{title} ({rows.length})</div>
      <div className="space-y-1">
        {rows.map((d) => {
          const isDraft = draftAware && d.status === "draft";
          const ref = d[noLabel] || d.id;
          return (
          <div key={d.id} className="flex items-center justify-between rounded-sm border border-gray-100 px-2 py-1.5 text-sm">
            <span className="lx-num font-medium">{d[noLabel] || (isDraft ? "Draft — no number yet" : "—")}{extraRef && extraRef(d) ? ` · ${extraRef(d)}` : ""}</span>
            <span className="text-xs text-gray-500">{fmtDate(d.date)} · {curSym(cur)} {fmtNum(d.total_amount)}</span>
            <span className="flex items-center gap-2">
              {isDraft ? (
                <>
                  <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700" data-testid={`draft-badge-${ref}`}>Draft</span>
                  <button className="text-gray-400 hover:text-[#1B4383]" data-testid={`edit-draft-${ref}`} title="Edit draft" onClick={() => onEditDraft(d)}><PencilSimple size={15} /></button>
                  <button className="rounded-sm bg-[#1B4383] px-2 py-0.5 text-xs font-semibold text-white hover:bg-[#122E5C]" data-testid={`post-draft-${ref}`} title="Confirm & post to GL" onClick={() => onPostDraft(d.id)}>Confirm &amp; Post</button>
                  <button className="text-gray-400 hover:text-red-500" data-testid={`del-${ref}`} onClick={() => onDel(d.id)}><Trash size={15} /></button>
                </>
              ) : (
                <>
                  <button className={`rounded-full px-2 py-0.5 text-xs font-semibold hover:underline ${payBadgeClass(d.payment_status)}`} data-testid={`pay-status-${d[noLabel]}`} onClick={() => onPayments({ ref: d[noLabel], payments: d.payments })}>{d.payment_status}</button>
                  {onEdit && d.payment_status === "Not Paid" && <button className="text-gray-400 hover:text-[#1B4383]" data-testid={`edit-${d[noLabel]}`} title="Edit invoice" onClick={() => onEdit(d)}><PencilSimple size={15} /></button>}
                  {pdf && <PrintButton path={`pdf/${pdf}/${d.id}`} testid={`dl-${d[noLabel]}`} className="text-gray-400 hover:text-[#1B4383]"><DownloadSimple size={16} /></PrintButton>}
                  {d.currency && d.currency !== "TZS" && onSetFx && <button className="rounded-sm border border-[#1B4383]/40 px-1.5 py-0.5 text-[10px] font-semibold text-[#1B4383] hover:bg-[#E6ECF5]" data-testid={`set-fx-${d[noLabel]}`} title="Owner: set FX rate (posts a correcting journal)" onClick={() => onSetFx(d)}>FX {d.fx_rate ? `@ ${d.fx_rate}` : "— set"}</button>}
                  {reverseEnabled && <ReverseActions inline sourceId={d.id} onDone={onReversed} />}
                  <button className="text-gray-400 hover:text-red-500" data-testid={`del-${d[noLabel]}`} onClick={() => onDel(d.id)}><Trash size={15} /></button>
                </>
              )}
            </span>
          </div>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="lx-label">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function OcrUpload({ onFile, loading, testid, label }) {
  return (
    <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-sm border border-[#1B4383] bg-white px-2.5 py-1 text-xs font-semibold text-[#1B4383] transition-colors hover:bg-[#1B4383] hover:text-white ${loading ? "pointer-events-none opacity-60" : ""}`} data-testid={testid}>
      {loading ? <CircleNotch size={14} className="animate-spin" /> : <Scan size={14} />}
      {loading ? "Scanning…" : label}
      <input type="file" className="hidden" accept="image/*,application/pdf" disabled={loading}
        onChange={(e) => { onFile(e.target.files[0]); e.target.value = ""; }} />
    </label>
  );
}

function UnbilledARPanel({ tid }) {
  const [f, setF] = useState(null);
  const load = () => api.get(`/transactions/${tid}/unbilled-ar`).then((r) => setF(r.data)).catch(() => {});
  useEffect(() => { load(); }, [tid]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!f || (f.accrued === 0 && f.cleared === 0)) return null;
  const trueUp = async () => {
    try { await api.post(`/transactions/${tid}/unbilled-ar/true-up`, {}); toast.success("Variance trued up to revenue"); load(); }
    catch (e) { toast.error(errMsg(e) || "Nothing to true up"); }
  };
  return (
    <div className="mt-3 rounded-sm border border-indigo-200 bg-indigo-50/50 p-3" data-testid="unbilled-ar-panel">
      <div className="lx-label mb-1 text-indigo-700">Unbilled AR — shipped-not-billed</div>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
        <span>Accrued: <span className="lx-num font-medium">{fmtNum(f.accrued)}</span></span>
        <span>Cleared: <span className="lx-num font-medium">{fmtNum(f.cleared)}</span></span>
        <span>Variance posted: <span className="lx-num font-medium">{fmtNum(f.variance_posted)}</span></span>
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${Math.abs(f.outstanding) < 0.01 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} data-testid="unbilled-ar-outstanding">Outstanding {fmtNum(f.outstanding)}</span>
        {Math.abs(f.outstanding) >= 0.01 && (
          <button className="text-xs font-semibold text-indigo-700 hover:underline" data-testid="unbilled-ar-trueup" onClick={trueUp}>Post variance true-up</button>
        )}
      </div>
    </div>
  );
}

